import { useState } from 'react';
import { supabase } from '../lib/supabase';
import type { NewsTag } from '../types/news';
import slugify from 'slugify';

export function useNewsTags() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTags = async () => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('news_tags')
        .select('*')
        .order('name', { ascending: true });

      if (fetchError) throw fetchError;

      return data as NewsTag[];
    } catch (err: any) {
      console.error('Error fetching tags:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const createTag = async (tag: Partial<NewsTag>) => {
    try {
      setLoading(true);
      setError(null);

      if (!tag.name) {
        throw new Error('Name is required');
      }

      const slug = slugify(tag.name, { lower: true, strict: true });

      const { data: newTag, error: insertError } = await supabase
        .from('news_tags')
        .insert([{ ...tag, slug }])
        .select()
        .single();

      if (insertError) throw insertError;

      return newTag;
    } catch (err: any) {
      console.error('Error creating tag:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateTag = async (id: string, updates: Partial<NewsTag>) => {
    try {
      setLoading(true);
      setError(null);

      if (updates.name) {
        updates.slug = slugify(updates.name, { lower: true, strict: true });
      }

      const { data: updatedTag, error: updateError } = await supabase
        .from('news_tags')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (updateError) throw updateError;

      return updatedTag;
    } catch (err: any) {
      console.error('Error updating tag:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteTag = async (id: string) => {
    try {
      setLoading(true);
      setError(null);

      const { error: deleteError } = await supabase
        .from('news_tags')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;
    } catch (err: any) {
      console.error('Error deleting tag:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    fetchTags,
    createTag,
    updateTag,
    deleteTag
  };
}